import { Component, Input, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';
import { SessionService } from '@monsido/core/session/session.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { LinksRepoService } from '@client/app/services/api/links-repo/links-repo.service';
import { MonDialog } from '@client/app/modules/dialog-upgrade-wrapper/mon-dialog.service';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { LinkContentComponent } from '@client/app/pages/qa/broken-links/links/link-content/link-content.component';

@Component({
    selector: 'broken-table',
    standalone: false,
    template: `
        <div class="px-4 qa-broken">
            <table-container [noResultMessage]="noResultMessage"
                             [collection]="items"
                             (onPageChange)="pageChange($event)"
                             (onPerPageChange)="perPageChange($event)"
                             [progress]="progress">
                <table class="table table-hover table-fixed table-vertial-align">
                    <thead>
                    <tr>
                        <th class="col-1 text-v-middle text-center">
                            <table-select-item-all
                                    class="inline-block"
                                    [(selectedItems)]="selected"
                                    (selectedItemsChange)="selectedChange.emit($event)"
                                    [items]="items">
                            </table-select-item-all>
                        </th>
                        <th class="col-16" [class.col-16]="showAutofix" [class.col-20]="!showAutofix">
                            <page-link-dropdown-all
                                    class="inline-block"
                                    [(selected)]="selected"
                                    (selectedChange)="selectedChange.emit($event)"
                                    [status]="type"
                                    (callback)="getPageCallback($event)">
                            </page-link-dropdown-all>
                            <span *ngIf="selected.length === 0">
                                <span
                                    monTableSort
                                    [monSortBy]="'url'"
                                    [monSortDirection]="currentSort.direction"
                                    (monOnSorted)="onSortPage.emit($event)"
                                    [monCurrentlySortedBy]="currentSort.by">
                                    {{ 'Broken link' | translate }}
                                </span>
                            </span>
                        </th>
                        <th class="col-1">&nbsp;</th>
                        <th class="col-4 text-right" *ngIf="showAutofix" translate>PageCorrect</th>
                        <th class="col-4 text-right" translate>Response code</th>
                        <th class="col-3 text-right" translate>Type</th>
                        <th class="col-4" *ngIf="showDocuments && type === 'dead' && !hidePagesColumn"></th>
                        <th class="col-4 text-right" *ngIf="type === 'dead' && !hidePagesColumn">
                            <span
                                monTableSort
                                [monSortBy]="'dead_on_pages_count'"
                                [monSortDirection]="currentSort.direction"
                                (monOnSorted)="onSortPage.emit($event)"
                                [monCurrentlySortedBy]="currentSort.by">
                                {{ 'Pages' | translate }}
                            </span>
                        </th>
                        <th class="col-4">&nbsp;</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr *ngFor="let link of items; trackBy: trackByLinkId">
                        <td class="text-v-middle text-center">
                            <table-select-item class="inline-block"
                                               [(selectedItems)]="selected"
                                               [item]="link"
                                               (selectedItemsChange)="selectedChange.emit($event)" />
                        </td>
                        <td>
                            <div class="text-ellipsis">
                                <a *ngIf="link.url" [href]="link.url" target="_blank" [attr.aria-describedby]="'linkOpensInNewTab'">{{ link.url }}</a>
                                <span *ngIf="!link.url" class="text-italic" translate>Unable to read link</span>
                            </div>
                            <label-table [assignedLabels]="link.labels"
                                         (getLabels)="getLabel()"
                                         [existingLabels]="labels"
                                         (onUpdateLabel)="updateLinksLabel(link)">
                            </label-table>
                        </td>
                        <td class="text-right">
                            <span class="fas fa-clock"
                                  aria-hidden="true"
                                  *ngIf="link.status == 'timeout'"
                                  [ngbTooltip]="'This link is marked as broken because it took more than 30 seconds to load' | translate"
                                  [triggers]="'focus hover'"
                                  [placement]="'top'">
                            </span>
                        </td>
                        <td class="text-right" *ngIf="showAutofix">
                            <page-fix-icon (cb)="getPageCallback($event)" type="link" [subType]="link.getPageFixType()" [model]="link" *ngIf="link.type !== 'form'" />
                        </td>
                        <td class="text-right">
                            <span *ngIf="link.status_code >= 0">{{ link.status_code}}</span>
                            <span *ngIf="link.status_code < 0 || link.status_code === null"
                                  translate
                                  [ngbTooltip]="'No response received' | translate"
                                  [triggers]="'focus hover'">
                                n/a
                            </span>
                        </td>
                        <td class="text-right">
                            <span class="label label-default">{{ link.type | capitalize }}</span>
                        </td>
                        <td class="text-right" *ngIf="showDocuments && type === 'dead' && !hidePagesColumn" [attr.aria-label]="'Document' | translate">
                            <documents-button
                                    (callback)="openPages(link, 'document')"
                                    [ngModel]="link.dead_on_documents_count"
                                    name="documentsCount">
                            </documents-button>
                        </td>
                        <td class="text-right" *ngIf="type === 'dead' && !hidePagesColumn">
                            <pages-button
                                    (callback)="openPages(link, 'html')"
                                    [text]="'Pages' | translate"
                                    [ngModel]="link.dead_on_pages_count"
                                    name="pagesCount">
                            </pages-button>
                        </td>
                        <td class="text-right" [attr.aria-label]="'Dropdown' | translate">
                            <page-link-dropdown [link]="link" (getPage)="getPageCallback($event)"></page-link-dropdown>
                        </td>
                    </tr>
                    </tbody>
                    <tfoot>
                        <tr *ngIf="selected.length > 0">
                            <td class="text-v-middle text-center col-1">
                                <table-select-item-all class="inline-block"
                                                   [(selectedItems)]="selected"
                                                   [items]="items">
                                </table-select-item-all>
                            </td>
                            <td class="col-16">
                                <page-link-dropdown-all class="inline-block"
                                                        [(selected)]="selected"
                                                        [status]="type"
                                                        (callback)="getPageCallback($event)">
                                </page-link-dropdown-all>
                            </td>
                            <td class="col-1 text-right">&nbsp;</td>
                            <td class="col-4 text-right" *ngIf="showAutofix"></td>
                            <td class="col-4 text-right"></td>
                            <td class="col-3"></td>
                            <td class="col-4" *ngIf="showDocuments && type === 'dead' && !hidePagesColumn"></td>
                            <td class="col-4 text-right" *ngIf="type === 'dead' && !hidePagesColumn"></td>
                            <td class="col-4">&nbsp;</td>
                        </tr>
                    </tfoot>
                </table>
            </table-container>
        </div>
    `,
})
export class BrokenTableComponent implements OnInit, OnChanges {
    @Input() selected: any = [];
    @Input() items: any[] = [];
    @Input() currentSort: any = {};
    @Input() type: string = '';
    @Input() progress: any = {};
    @Input() labels: any[] = [];
    @Input() selectedLabels: any[] = [];
    @Input() hidePagesColumn = false;

    @Output() selectedChange = new EventEmitter<any[]>();
    @Output() onPageChange = new EventEmitter();
    @Output() onPerPageChange = new EventEmitter();
    @Output() onSortPage = new EventEmitter();
    @Output() getPage = new EventEmitter();
    @Output() getLabels = new EventEmitter();

    public showDocuments: boolean = false;
    public showAutofix: boolean = false;
    public noResultMessage: string = '';

    constructor (
        private sessionService: SessionService,
        private monDialog: MonDialog,
        private activeFeatureService: ActiveFeatureService,
        private translateService: TranslateService,
        private linksRepoService: LinksRepoService,
        private monEventService: MonEventService,
    ) {}

    ngOnInit (): void {
        this.showDocuments = this.sessionService.domain?.scan?.scan_documents || false;
        this.showAutofix = this.activeFeatureService.isFeatureActive('page_fix_qa_links');
        this.setNoResultMessage();
    }

    ngOnChanges (): void {
        this.setNoResultMessage();
    }

    getPageCallback = ($event) => {
        this.getPage.emit($event);
    };

    private setNoResultMessage (): void {
        switch (this.type) {
            case 'dead':
                this.noResultMessage = this.translateService.getString('Good job! No issues were found');
                break;
            case 'ignored':
                this.noResultMessage = this.translateService.getString('No Ignored Broken Links');
                break;
            case 'fixed':
                this.noResultMessage = this.translateService.getString('No Broken Links marked as Fixed');
                break;
            default:
                this.noResultMessage = this.translateService.getString('Good job! No issues were found');
                break;
        }
    }

    pageChange (page: any): void {
        this.onPageChange.emit(page);
    }

    perPageChange (size: any): void {
        this.onPerPageChange.emit(size);
    }

    openPages (link: any, type: string): void {
        const params = {
            body: LinkContentComponent,
            size: 'md',
            data: { link: link, type: type },
        };

        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, { params });
    }

    updateLinksLabel (link: any): void {
        const params = {
            id: link.id,
            labels: link.labels.map((label: any) => {
                return label.id;
            }),
        };
        this.linksRepoService.update(params).then(() => {}, () => {});
    }

    getLabel (): void {
        this.getLabels.emit();
    }

    trackByLinkId (index: number, link: any): any {
        return link.id;
    }
}
